// 📐 16:9 자막바 기하 SSOT (2026-08-05 가독성 수리 · P1-3/P2-1 봉합)
//
// 왜 별도 순수 모듈인가: 자막 크기 clamp(story-data-edit)와 자막바 렌더(Overlay169Template)가 **같은
// 식**을 봐야 하는데, 후자는 remotion `loadFont(...)` 를 모듈 로드 시점에 실행하는 무거운 렌더러라
// 인스펙터 편집 경로가 import 할 수 없다. 그래서 "숫자와 식"만 여기 둔다(의존성 0).
//
// ── 좌표계 ──────────────────────────────────────────────────────────────────────
// Overlay169Template 은 1920×1080 을 기준 좌표계로 쓰고 실제 렌더는 전부 ×S(=width/1920) 다.
// 자막바의 모든 항(폰트·패딩·중심)이 같은 S 를 타므로 **프레임 이탈 판정은 S 와 무관**하다 →
// 여기서는 1920×1080 고정으로 계산한다.
//
// ── 자막바 실측 기하(Overlay169Template 렌더 코드에서 그대로 옮김) ───────────────
//   폰트 픽셀      capSize   = body.size × S
//   글줄 높이      lineHeight: 1.35            (자막 텍스트 div)
//   바 높이        barH      = rows × capSize × 1.35 + 2 × padY × S
//   바 세로 중심   centerY   = height × skin.captionCenterY   (transform: translateY(-50%))
//   바 하단        bottom    = centerY + barH / 2
//   바 최대 폭     maxWidth  = width × 0.86  (박스 기준·content-box 라 좌우 패딩은 별도)

// 📐 세이프존 정본 — 그 자신도 의존성 0 순수 모듈이라 이 파일의 «무거운 렌더러를 끌어오지 않는다» 원칙이 유지된다.
import { SAFE_ZONE } from "../safe-zone";

/** 기준 좌표계 — Overlay169Template 의 S 정의(width/1920)와 짝. */
export const OVERLAY169_FRAME = { width: 1920, height: 1080 } as const;

/** 자막 텍스트 div 의 lineHeight(렌더 하드코딩 1.35). */
export const CAPTION_LINE_HEIGHT = 1.35;

/** 자막바 박스 maxWidth 비율(렌더 하드코딩 `width * 0.86`). */
export const CAPTION_BAR_MAX_WIDTH_RATIO = 0.86;

/**
 * 접기 폭이 CSS 실효 폭 대비 남겨야 하는 안전 여유(비율).
 *
 * ★왜 필요한가★: 접기(foldRows)는 `visualEmWidth` 라는 **근사 폭**으로 자르는데 실제 렌더는 브라우저
 *   글자 폭이다. 접기 목표가 CSS 실효 폭에 붙어 있으면 근사 오차만큼 한 행이 실효 폭을 넘고, 그 순간
 *   브라우저가 **행을 한 번 더 접어** 계획에 없던 줄이 생긴다(3줄 계획 → 4줄 렌더 → 바가 커져 프레임
 *   이탈). 실제로 목표 0.80 vs 실효 0.81 = 여유 1.25% 였다.
 */
export const CAPTION_FOLD_SAFETY = 0.08;

/** 자막바 기하 중 프레임 이탈 판정에 필요한 최소 투영(OverlaySkin 의 구조적 부분집합). */
export interface CaptionBarGeometry {
  captionCenterY: number;
  captionPadY: number;
  captionPadX: number;
  captionMaxRows: number;
}

/**
 * 📐 자막바 바닥이 넘지 말아야 할 선(정규화) = 세이프존 16:9 core 하단(82.87%).
 *   그 아래 10.65% 는 재생바·컨트롤, 82.87~89.35% 는 제목·설명이 뜨는 빗금 구역이다(데드존 가이드 실측).
 */
export const OVERLAY169_CAPTION_SAFE_BOTTOM = 1 - SAFE_ZONE["16:9"].core.bottom;

/**
 * 📐 **세이프존으로 접힌 자막바 세로 중심**(정규화) — 회장님 2026-09-23 전역 적용 2단계 · 자막 «가안».
 *
 * 스킨의 `captionCenterY`(141종 중 131종이 0.9)는 바 바닥을 92~95% 에 둬 재생바·설명 자리에 들어갔다.
 * **저장값(스킨 상수)은 안 고치고** 그리는 순간 «바 바닥 ≤ 82.87%» 가 되도록 **위로만** 민다 — 줄 수가
 * 늘면 바가 위로 자라고 바닥은 선에 붙는다. 이미 선 위인 스킨은 그대로다.
 * ★렌더(`Overlay169Template`)·정밀 편집 핸드오프(`precision-overlay-caption`)·경계 판정(아래 두 함수)이
 *   전부 이 함수를 부른다★ — 한 곳만 접으면 미리보기·편집·경계가 갈린다(#5450 에서 실제로 겪은 누락).
 */
export function foldedCaptionCenterY(geom: Pick<CaptionBarGeometry, "captionCenterY" | "captionPadY">, rows: number, bodySize: number): number {
  const { height } = OVERLAY169_FRAME;
  const barH = rows * bodySize * CAPTION_LINE_HEIGHT + 2 * geom.captionPadY;
  return Math.min(geom.captionCenterY, OVERLAY169_CAPTION_SAFE_BOTTOM - barH / (2 * height));
}

/** 자막바 하단 y(기준 좌표계 px) — 세이프존으로 접힌 중심 기준. 이 값이 frame.height 를 넘으면 잘린다. */
export function captionBarBottom(geom: Pick<CaptionBarGeometry, "captionCenterY" | "captionPadY">, rows: number, bodySize: number): number {
  const { height } = OVERLAY169_FRAME;
  const barH = rows * bodySize * CAPTION_LINE_HEIGHT + 2 * geom.captionPadY;
  return height * foldedCaptionCenterY(geom, rows, bodySize) + barH / 2;
}

/**
 * 자막바 **상단의 정규화 y**(0..1 · 프레임 높이 대비). `captionBarBottom` 과 같은 바 기하(중심 ∓ barH/2)를
 * 공유하지만 **단위가 다르다** — `captionBarBottom` 은 px 하단, 이 함수는 **정규화** 상단(÷ height)이다.
 * (안전선 `infographicCaptionSafeTop` 이 정규화 y 라 직접 비교하려고 비율로 돌려준다.)
 *
 * ★용도★: 인포그래픽 자막 안전선(16:9 = `infographicCaptionSafeTop("16:9")`)이 **이 값보다 아래**(≤)여야
 *   자막바 겹침 경고를 놓치지 않는다. 안전선은 화면비 상수 하나라 스킨·body.size 를 모르므로, 소비처가
 *   아니라 **테스트가** 전 스킨의 최빡(가장 위로 자란 바 = 가장 작은 상단 y)을 이 함수로 재서
 *   `상수 ≤ 최빡` 을 못 박는다(anti-drift).
 */
export function captionBarTopRatio(geom: Pick<CaptionBarGeometry, "captionCenterY" | "captionPadY">, rows: number, bodySize: number): number {
  const { height } = OVERLAY169_FRAME;
  const barH = rows * bodySize * CAPTION_LINE_HEIGHT + 2 * geom.captionPadY;
  return foldedCaptionCenterY(geom, rows, bodySize) - barH / (2 * height);
}

/**
 * 이 스킨에서 **프레임 안에 남는** body.size 상한(정수 내림).
 *
 * 유도: `H·cy + (rows·size·1.35 + 2·padY)/2 ≤ H`
 *   ⇒ `size ≤ (2·H·(1 − cy) − 2·padY) / (1.35·rows)`
 *
 * ★rows 는 스킨의 captionMaxRows★ — 접기 캡이 곧 최대 물리 줄이라 최악의 바 높이를 준다.
 */
export function maxBodySizeForBar(geom: Pick<CaptionBarGeometry, "captionCenterY" | "captionPadY" | "captionMaxRows">): number {
  const { height } = OVERLAY169_FRAME;
  const numerator = 2 * height * (1 - geom.captionCenterY) - 2 * geom.captionPadY;
  return Math.floor(numerator / (CAPTION_LINE_HEIGHT * Math.max(1, geom.captionMaxRows)));
}

/**
 * 자막 텍스트가 실제로 쓸 수 있는 **CSS 실효 폭 비율** — 박스 maxWidth 에서 좌우 패딩을 뺀 값.
 * (렌더는 content-box 라 padding 이 maxWidth 안쪽을 먹는다.)
 */
export function captionCssContentRatio(geom: Pick<CaptionBarGeometry, "captionPadX">): number {
  return CAPTION_BAR_MAX_WIDTH_RATIO - (2 * geom.captionPadX) / OVERLAY169_FRAME.width;
}

/** 이 스킨에서 접기 목표로 써도 안전한 최대 폭 비율(실효 폭 − 안전 여유). */
export function safeCaptionFoldRatio(geom: Pick<CaptionBarGeometry, "captionPadX">): number {
  return captionCssContentRatio(geom) * (1 - CAPTION_FOLD_SAFETY);
}

// ── 편집 clamp 가 쓰는 보수 기하 ────────────────────────────────────────────────
//
// 편집 경로(story-data-edit)는 스킨 상수 테이블을 import 할 수 없으므로(위 사유) **가장 빡빡한
// 기하**를 기준으로 clamp 한다. 그 값이 실제로 최솟값인지는 Overlay169Template 쪽 유닛 테스트가
// 전 스킨을 훑어 못 박는다(래칫 — 더 빡빡한 스킨이 추가되면 실패한다).
//
// ★현재 최빡 = 브리핑·설계도 5종(3줄 캡 · centerY 0.9 · padY 16) → 상한 45.
//   같은 스킨이 2줄이던 시절 상한은 68 이었다 — **3줄 허용이 크기 여유를 먹은 것**이고, 이는
//   의도한 교환이다(뭉쳐서 화면 밖으로 흐르는 것보다 3줄이 낫다).
export const TIGHTEST_CAPTION_BAR_GEOMETRY: CaptionBarGeometry = {
  captionCenterY: 0.9,
  captionPadY: 16,
  captionPadX: 48,
  captionMaxRows: 3,
};

/** 편집 clamp 의 가로 상한(=가장 빡빡한 스킨 기준). 현재 45. */
export const CONSERVATIVE_MAX_BODY_SIZE_169 = maxBodySizeForBar(TIGHTEST_CAPTION_BAR_GEOMETRY);

/**
 * 가로 자막 크기 하한 — 1080p 에서 28px(≈2.6%H). 이보다 작으면 TV·모바일 어디서도 안 읽힌다.
 * ★저장분이 28 미만인 16:9 는 세로 clamp(≤28)에 잘못 걸렸던 **비정상 데이터**다 — 그 경우 28 로
 *  올라가는 "점프"는 회귀가 아니라 정상화다(P3-9 판정).
 */
export const MIN_BODY_SIZE_169 = 28;

// ── 📐 전체 화면 장표 ↔ 자막 띠 (#6119 · 2026-10-09) ──────────────────────────────────
//
// #5460(09-24)으로 16:9 자막 띠가 82.87% 선 위로 올라왔는데 옛 설계도 가족(기관·현장·기업·강의 브리핑)의 전체 화면
// 장표는 그 자리를 비우지 않아 장표 아랫줄이 자막에 덮였다(삼성생명 43편 중 32편 51장면). 모션 무대 가족은 이미
// 자막 윗선을 받아 도형을 비켜 세운다 — 전체 화면 장표도 같은 윗선을 받아 «세이프존 위선 ~ 자막 윗선 − 여백» 안에 앉힌다.
// ★바꾸는 것은 «넘는 장면»뿐★ — 글자 덩어리(실제 배치 · 장표 층이 잰다)가 자막 윗선에 안 닿으면 null = 종전 그대로(바이트 동일).
// 닿으면 먼저 위로만 올리고(크기 그대로), 그래도 안 들어가면 덩어리 전체를 줄인다(가상 화면 scale — VM 한쪽 패널과 같은 길).

/** 장표 글자 덩어리 상자(장표 층 좌표 px · 변환 무시 배치값). */
export interface SlideContentExtent { top: number; bottom: number; left: number; right: number }

/** 이 이상 줄이지 않는다 — 더 작으면 글자가 안 읽힌다(VM 한쪽 패널 하한과 같은 값). */
export const SLIDE_CAPTION_FIT_MIN_SCALE = 0.5;

/**
 * 전체 화면 장표를 자막 윗선 위에 앉히는 변환(transform-origin 0 0 · `translate(tx,ty) scale(k)`). 안 닿으면 null.
 *  - `pad`: 덩어리 위아래 여유(장표 부품의 천천히 다가가기·떠다니기가 배치값보다 커 보이는 몫).
 *  - `gap`: 자막 띠 위 빈칸.
 *  - `push`: 바깥에서 덩어리를 한 번 더 키우는 변환(모션 타이포 카메라 scale · 원점 y) — 그 변환을 거친 뒤에도 윗선 위가 되게 거꾸로 잡는다.
 */
export function slideCaptionFit(o: {
  extent: SlideContentExtent; captionTop: number; safeTop: number; gap: number; pad: number;
  push?: { scale: number; originY: number };
}): { k: number; tx: number; ty: number } | null {
  const { extent: e, captionTop, safeTop, gap, pad } = o;
  if (![e.top, e.bottom, e.left, e.right, captionTop, safeTop].every(Number.isFinite) || e.bottom <= e.top) return null;
  const P = o.push && o.push.scale > 0 ? o.push.scale : 1, oy = o.push ? o.push.originY : 0;
  const toPre = (y: number) => oy + (y - oy) / P; // 바깥 변환 뒤 화면 y → 변환 전 y
  const top = e.top - pad, bottom = e.bottom + pad;
  if (oy + (bottom - oy) * P <= captionTop) return null; // 안 닿는다 = 종전 그대로
  const yT = toPre(safeTop), yB = toPre(captionTop - gap), avail = yB - yT, h = bottom - top;
  if (!(avail > 0) || !(h > 0)) return null;
  const k = Math.max(SLIDE_CAPTION_FIT_MIN_SCALE, Math.min(1, Math.floor((avail / h) * 1000) / 1000));
  // 크기 그대로면 «들어갈 만큼만» 위로(제자리에 가장 가깝게) · 줄였으면 위선에 붙인다.
  const newTop = k === 1 ? Math.min(top, yB - h) : yT;
  const cx = (e.left + e.right) / 2;
  const r2 = (v: number) => Math.round(v * 100) / 100;
  return { k, tx: r2(cx * (1 - k)), ty: r2(newTop - k * top) };
}
